<script setup lang="ts">
    import {
        useGetCoachingForm,
        useGetCoachingForms,
    } from '@/js/services/api/coachingForm/coachingFormQueries';
    import { computed, ref, watch } from 'vue';
    import CoachingFormController from '../../coaching/form/CoachingFormController.vue';
    import { __ } from '@/js/stores/expressions';
    import RadioOption from '../../RadioOption.vue';
    import { useCoachInteractionStore } from '@/js/stores/coach-interaction';
    import EchoButton from '../../EchoButton.vue';
    import EchoTooltip from '../../EchoTooltip.vue';
    import LoadingDots from '../../LoadingDots.vue';
    import {
        useLazyQueryGetProtocol,
        useMutationPatchProtocol,
        useMutationPostAIProtocol,
        useMutationPostApproveProtocols,
        useMutationPostProtocol,
    } from '@/js/services/api/protocol/protocolQueries';
    import {
        CoachingType,
        RequestPatchProtocol,
        RequestPostAICoachingProtocol,
        RequestPostProtocol,
    } from '@/js/services/api/protocol/protocolModel';
    import {
        generatePostProtocolRequest,
        generatePatchProtocolRequest,
    } from '../../coaching/form/coachingForm.utils';
    import SelectInput from '../../SelectInput.vue';
    import { useSuccessDialog } from '@/js/stores/success-dialog';
    import AIBuddyIcon from '../../AIBuddyIcon.vue';
    import AIBuddyLoadingScreen from '../coaching/AIBuddyLoadingScreen.vue';
    import dayjs from 'dayjs';
    import EchoLabel from '../../EchoLabel.vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { useProtocolLock } from '@/js/composables/useProtocolLock';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import { CheckIcon, HourglassIcon, PencilIcon, XIcon } from '@lucide/vue';

    const { hasPermission } = usePermissions();

    const canCoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ManualCoaching
    );

    const canAICoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.AICoaching
    );

    const canApproveProtocols = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ApproveProtocols
    );

    const canEditAiCoach = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.EditAiCoach
    );

    const { data: forms, isLoading } = useGetCoachingForms();

    const props = defineProps<{
        interactionId: string;
        fullWidth?: boolean;
    }>();

    const selectedFormId = ref<string>('');

    const protocolId = ref<string>('');
    const editAIProtocol = ref<boolean>(false);

    const {
        data: selectedForm,
        isFetching: isLoadingSelectedForm,
        refetch: refetchSelectedForm,
    } = useGetCoachingForm(selectedFormId);

    const { mutateAsync: postProtocol, isPending: postProtocolIsPending } =
        useMutationPostProtocol();

    const { mutateAsync: patchProtocol, isPending: patchProtocolIsPending } =
        useMutationPatchProtocol(protocolId);

    const { mutateAsync: submitAIProtocol } = useMutationPostAIProtocol();

    const { mutateAsync: fetchProtocol } = useLazyQueryGetProtocol();

    const {
        mutateAsync: approveProtocol,
        isSuccess: isApproved,
        reset: resetApproval,
    } = useMutationPostApproveProtocols(() => [protocolId.value]);

    // The approval flag belongs to the protocol it approved, not the next one coached here.
    watch(protocolId, () => resetApproval());

    const aiLoading = ref(false);

    const aiForms = computed(() => {
        if (!forms.value) return [];

        return forms.value.forms
            .filter((form) => form.isActive && form.useAi)
            .sort((a, b) => a.title.localeCompare(b.title));
    });

    const manualForms = computed(() => {
        if (!forms.value) return [];

        return forms.value.forms
            .filter((form) => form.isActive && !form.useAi)
            .sort((a, b) => a.title.localeCompare(b.title));
    });

    watch(aiForms, (newVal) => {
        if (newVal.length > 0 && !isLoading.value) {
            selectedFormId.value = newVal[0].id;
        }
    });

    watch(selectedFormId, (newVal) => {
        if (newVal) {
            refetchSelectedForm();
            protocolId.value = '';
        }
    });

    const coachInteractionStore = useCoachInteractionStore();

    // Approving here updates the protocol only on the next fetch, so the local flag unlocks it at once.
    const { lockReason } = useProtocolLock(() => ({
        ...coachInteractionStore.protocol,
        approved: isApproved.value || coachInteractionStore.protocol.approved,
    }));

    const canSaveProtocol = computed(() =>
        coachInteractionStore.protocol.createdByAi ? canEditAiCoach.value : canCoach.value
    );

    const successDialogStore = useSuccessDialog();

    const onSubmit = async () => {
        if (coachInteractionStore.isValid && selectedFormId.value) {
            const postRequest: RequestPostProtocol = generatePostProtocolRequest(
                coachInteractionStore.protocol,
                props.interactionId,
                selectedFormId.value
            );

            const patchRequest: RequestPatchProtocol = generatePatchProtocolRequest(
                coachInteractionStore.protocol
            );

            if (editAIProtocol.value) {
                await patchProtocol(patchRequest);
            } else {
                const res = await postProtocol(postRequest);
                protocolId.value = res.id;
            }

            editAIProtocol.value = false;

            fetchProtocol(protocolId.value).then((res) => {
                if (res.protocol) {
                    coachInteractionStore.setProtocol(selectedForm.value!, res.protocol);
                }
            });

            successDialogStore.openDialog({
                message: __('coaching-success'),
            });
        }
    };

    const onCoachWithAI = async () => {
        if (selectedFormId.value) {
            const req: RequestPostAICoachingProtocol = {
                formId: selectedFormId.value,
                interactionPartId: props.interactionId,
            };
            try {
                aiLoading.value = true;
                const res = await submitAIProtocol(req);
                if (res) {
                    protocolId.value = res.data;

                    fetchProtocol(protocolId.value).then((res) => {
                        if (res.protocol) {
                            coachInteractionStore.setProtocol(selectedForm.value!, res.protocol);

                            successDialogStore.openDialog({
                                message: __('ai-coaching-success'),
                            });
                        }
                    });
                }
            } catch (error) {
                console.error('Error submitting AI coaching protocol:', error);
            } finally {
                aiLoading.value = false;
            }
        }
    };
</script>
<template>
    <div class="border-b border-gray-lightest pb-8">
        <div class="flex justify-start items-center gap-9">
            <div class="flex-grow max-w-lg">
                <SelectInput
                    id="coaching-form-select"
                    v-model="selectedFormId"
                    class="w-full"
                    :aria-label="__('schedule-coaching-form-select-form-title')"
                >
                    <option value="">
                        {{ __('schedule-coaching-form-select-form-title') }}
                    </option>
                    <optgroup v-if="aiForms.length > 0" :label="__('ai-forms')">
                        <option v-for="form in aiForms" :key="form.id" :value="form.id">
                            {{ form.title }}
                        </option>
                    </optgroup>
                    <optgroup v-if="manualForms.length > 0" :label="__('manual-forms')">
                        <option v-for="form in manualForms" :key="form.id" :value="form.id">
                            {{ form.title }}
                        </option>
                    </optgroup>
                </SelectInput>
            </div>
            <div class="flex items-center gap-4 flex-wrap">
                <template v-if="protocolId === ''">
                    <RadioOption
                        id="sideBySide"
                        v-model="coachInteractionStore.protocol.coachingType"
                        name="coachingType"
                        :label="__('side-by-side')"
                        :value="CoachingType.SIDE_BY_SIDE"
                    />
                    <RadioOption
                        id="remote"
                        v-model="coachInteractionStore.protocol.coachingType"
                        name="coachingType"
                        :label="__('remote')"
                        :value="CoachingType.REMOTE"
                    />
                    <div class="flex gap-1">
                        <AIBuddyIcon />
                        <EchoButton
                            v-if="canAICoach && selectedForm && selectedForm.useAi"
                            :disabled="
                                !selectedFormId || !coachInteractionStore.protocol.coachingType
                            "
                            @click="onCoachWithAI"
                        >
                            {{ __('use-ai') }}
                        </EchoButton>
                    </div>
                </template>
                <template v-else>
                    <EchoTooltip
                        v-if="canAICoach && !editAIProtocol"
                        :label="lockReason ?? ''"
                        :disabled="!lockReason"
                        :tabindex="lockReason ? 0 : undefined"
                    >
                        <EchoButton
                            :aria-label="__('edit-ai-protocol')"
                            :disabled="!!lockReason"
                            @click="editAIProtocol = true"
                        >
                            {{ __('edit-ai-protocol') }}
                            <template #icon-right><PencilIcon /></template>
                        </EchoButton>
                    </EchoTooltip>
                    <EchoButton
                        v-else
                        :aria-label="__('edit-ai-protocol')"
                        @click="editAIProtocol = false"
                    >
                        {{ __('cancel-edit-ai-protocol') }}
                        <template #icon-right><XIcon /></template>
                    </EchoButton>
                    <EchoButton
                        v-if="
                            (canApproveProtocols || coachInteractionStore.protocol.isSelfCoached) &&
                            coachInteractionStore.protocol.requiresApproval &&
                            !coachInteractionStore.protocol.approved &&
                            !isApproved
                        "
                        variant="primary"
                        @click="approveProtocol()"
                    >
                        {{ __('protocol-approve') }}
                        <template #icon-right><HourglassIcon /></template>
                    </EchoButton>
                    <div
                        v-else-if="coachInteractionStore.protocol.approved || isApproved"
                        class="text-body-bold flex items-center justify-center gap-1 w-30"
                    >
                        {{ __('protocol-approved') }}
                        <CheckIcon class="size-5" />
                    </div>
                </template>
            </div>
        </div>
    </div>
    <div class="py-8 w-full">
        <div
            v-if="selectedFormId && !isLoadingSelectedForm && selectedForm"
            :class="fullWidth ? 'w-full' : 'w-full max-w-360'"
        >
            <div class="flex justify-between items-baseline mb-2">
                <p
                    v-if="
                        coachInteractionStore.protocol.submittedBy &&
                        coachInteractionStore.protocol.createdAt
                    "
                    class="text-caption mb-2"
                >
                    {{
                        __('protocol-detail-created-by', {
                            name: coachInteractionStore.protocol.submittedBy,
                            date: dayjs(coachInteractionStore.protocol.createdAt).format(
                                'YYYY-MM-DD HH:mm:ss'
                            ),
                        })
                    }}
                </p>
                <EchoLabel v-if="coachInteractionStore.protocol.createdByAi" :ai="true">
                    {{
                        coachInteractionStore.protocol.editedBy
                            ? __('coached-by-ai-buddy-edited')
                            : __('coached-by-ai-buddy')
                    }}
                </EchoLabel>
            </div>
            <CoachingFormController
                :form="selectedForm"
                :preview="selectedForm.useAi && !editAIProtocol"
            />
            <div
                v-if="!selectedForm.useAi || (selectedForm.useAi && protocolId && editAIProtocol)"
                class="flex justify-end py-4"
            >
                <EchoButton
                    :loading="postProtocolIsPending || patchProtocolIsPending"
                    :disabled="!canSaveProtocol || !coachInteractionStore.isValid"
                    @click="onSubmit"
                >
                    {{ __('save') }}
                    <template #icon-right><CheckIcon /></template>
                </EchoButton>
            </div>
        </div>
        <div v-else-if="isLoadingSelectedForm" class="h-32 flex items-center justify-center">
            <LoadingDots />
        </div>
    </div>
    <AIBuddyLoadingScreen :show="aiLoading" />
</template>
